import {
  CSSProperties,
  MouseEvent,
  useContext,
  useEffect,
  useState,
} from 'react'

import {
  CardReactions,
  PostContent,
  PostImagePreview,
  TogetherTypes,
  abbreviateNumber,
  Icon,
} from '@hhgtech/hhg-components'
import { useSSOV2Store } from '@hhgtech/hhg-components/ssoV2'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Plus } from '@hhgtech/icons/core'
import { Comment } from '@hhgtech/icons/other'
import { Heart } from '@hhgtech/icons/other'
import { Box, BoxProps, Flex } from '@mantine/core'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import {
  getHelloSitesUrl,
  getUrlToRedirectToSSOWithReturnUrl,
} from 'api/utils/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { CardReactionType } from 'components/pages/expert/_cardPost'
import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { IS_SSOV2_ENABLED } from 'interfaces/constants'
import { AppContext } from 'state/appContext'

import {
  StyledBody,
  StyledCardAuthor,
  StyledContent,
  StyledHeader,
  StyledOverlayLink,
  StyledPostContentWrapper,
  StyledRow,
  StyledThumb,
} from './index.styled'
import useStyles from './index.styles'
import { CommunityBtn } from '../communityBtn'

export type RelevantCardPostType = {
  joinedCommunity?: boolean
} & TogetherTypes.Post

export type Props = {
  post: RelevantCardPostType
  className?: string
  shareUrl?: string
  title?: string
  style?: CSSProperties
  jointCommunityBtn?: boolean
  joinCommunity?: (id: number | string) => Promise<any>
} & BoxProps

export const Item = ({
  post,
  jointCommunityBtn,
  joinCommunity,
  styles,
  className,
  classNames,
  ...rest
}: Props) => {
  const { state } = useContext(AppContext)
  const { isLoggedIn } = state || {}
  const {
    likesCount: likeCountProp = 0,
    liked,
    joinedCommunity,
    commentsCount = 0,
  } = post
  const [isFollowing, setIsFollowing] = useState<boolean>(false)

  const { locale } = useRouterExtend()
  const [isLike, setLike] = useState(false)
  const [firstMount, setFirstMount] = useState(true)
  const [likeCount, setLikeCount] = useState(likeCountProp)
  const { classes, cx, theme } = useStyles(undefined, {
    name: 'CarePathCommunityWithPostsItem',
    styles,
    classNames,
  })
  const postRelateiveHref = `/community/${post?.community?.slug || '_'}/${
    post?.slug || ''
  }/`
  const openPost = (e?: MouseEvent<HTMLAnchorElement>) => {
    e?.preventDefault()
    if (!isLoggedIn) {
      if (IS_SSOV2_ENABLED) {
        useSSOV2Store.getState().triggerLogin({
          returnUrl: `${getHelloSitesUrl(
            locale,
          )}/community/on-boarding/?returnUrl=/${post?.community?.slug}/${
            post?.slug
          }`,
        })
      } else {
        window.location.href = getUrlToRedirectToSSOWithReturnUrl(
          `${getHelloSitesUrl(locale)}/community/on-boarding/?returnUrl=/${
            post?.community?.slug
          }/${post?.slug}`,
        )
      }
    } else {
      window.location.href = postRelateiveHref
    }
  }
  const hasPhoto = (post?.photos || []).length > 0
  const author = post?.author
  const name = author?.name || author?.username || ''
  const username = author?.username || author?.name || ''
  const avatar = author?.avatar
  const authorProps: any = {
    isAnonymous: post?.isAnonymous,
    avatar: avatar,
    name: name,
    username: username,
    email: author?.email,
    role: author?.role,
    timeText: post?.createdTime,
    community: post?.community,
    showDetailPopup: true,
    userId: author?.id,
    profileModalProps: {
      zIndex: 1000,
    },
  }
  const handleLikeClick = async () => {
    if (!isLoggedIn) {
      post?.community?.slug &&
        redirectToSSOWithReturnUrl(
          `${getHelloSitesUrl(locale)}/community/on-boarding/?returnUrl=/${
            post.community.slug
          }`,
        )
    } else {
      await callApiNew(
        PATHS.TOGETHER.POST_TOGETHER_LIKE_POST,
        {
          params: {
            postId: post.id,
          },
        },
        'together',
      )
      setLike((pre) => !pre)
    }
  }

  useEffect(() => {
    setLike(Boolean(post?.liked))
  }, [post])

  useEffect(() => {
    !firstMount &&
      (isLike
        ? setLikeCount(liked ? likeCountProp : likeCountProp + 1)
        : setLikeCount(likeCountProp ? likeCountProp - 1 : likeCountProp))
    setFirstMount(false)
  }, [isLike])

  useEffect(() => {
    setIsFollowing(Boolean(joinedCommunity))
  }, [joinedCommunity])

  const tracking = {
    'data-event-category': 'Together Embedded',
    'data-event-action': 'Click Post',
    'data-event-label': `https://${domainLocales[locale]}${postRelateiveHref}`,
  }

  return (
    <Box className={cx(classes.root, className)} {...rest}>
      <Box>
        <StyledHeader>
          <StyledCardAuthor {...authorProps} />
        </StyledHeader>
        <StyledBody>
          <StyledRow>
            <StyledContent data-has-thumb={hasPhoto}>
              <Text as="h4" size="s3">
                {post?.title}
              </Text>
              <StyledPostContentWrapper>
                <PostContent
                  trackingCategory={'Together Embedded'}
                  url=""
                  className="post-content-wrapper"
                  description={post?.description}
                  disableShortenContent={false}
                  mentions={post?.mentions}
                  maxChars={hasPhoto ? 100 : 150}
                />
                <StyledOverlayLink
                  href={postRelateiveHref}
                  {...tracking}
                  onClick={(e) => {
                    openPost(e)
                  }}
                />
              </StyledPostContentWrapper>
            </StyledContent>
            {hasPhoto && (
              <StyledThumb
                className="cursor-pointer"
                onClick={(e) => {
                  openPost(e as any)
                }}
              >
                <PostImagePreview images={post.photos} />
              </StyledThumb>
            )}
          </StyledRow>
        </StyledBody>
      </Box>
      <CardReactions
        // postUrl={postHref}
        trackingCategory={'Together Embedded'}
        trackingLabel={`https://${domainLocales[locale]}${postRelateiveHref}`}
        type={CardReactionType.post}
        // viewCount={locale !== LOCALE.Malaysia ? post. : undefined}
        loveCount={likeCount}
        replyCount={post?.commentsCount}
        className={`post-reaction`}
        liked={isLike}
        hasShare={false}
        onCommentClick={() => {
          openPost()
        }}
        onLikeClick={handleLikeClick}
      >
        {jointCommunityBtn ? (
          <Flex align={'center'} justify={'space-between'}>
            {post?.community?.id && (
              <CommunityBtn
                isFollowing={isFollowing}
                community={post?.community}
                joinCommunity={joinCommunity}
                onJoinCommunity={() => setIsFollowing(true)}
                variant="white"
                leftIcon={
                  !isFollowing ? (
                    <Plus color="currentColor" size={18} />
                  ) : undefined
                }
                rightIcon={
                  isFollowing ? (
                    <Icon.ArrowRightTail useCurrentColor size={16} />
                  ) : undefined
                }
                styles={{
                  root: {
                    paddingLeft: 0,
                  },
                  leftIcon: {
                    marginRight: '4px',
                  },
                  rightIcon: {
                    marginLeft: '6px',
                  },
                }}
              />
            )}
            <Box className={classes.customReact}>
              <CardReactions.LikeWrapper {...tracking}>
                <Box className={classes.reactItem}>
                  <Heart
                    isSolid={isLike}
                    color={isLike ? theme.colors.red[8] : undefined}
                  />
                  <Text size="p4">{abbreviateNumber(likeCount)}</Text>
                </Box>
              </CardReactions.LikeWrapper>
              <CardReactions.CommentWrapper {...tracking}>
                <Box className={classes.reactItem}>
                  <Comment size={22} />
                  <Text size="p4">{abbreviateNumber(commentsCount)}</Text>
                </Box>
              </CardReactions.CommentWrapper>
            </Box>
          </Flex>
        ) : undefined}
      </CardReactions>
    </Box>
  )
}
